import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { motion, AnimatePresence, MotionValue } from 'framer-motion';
import { useSwipeCard } from '../../../../hooks/useSwipeCard';
import { useSwipeOnboarding, SwipeOnboardingContext } from '../../../../hooks/useSwipeOnboarding';
import { useAnalytics } from '../../../../contexts/AnalyticsContext';
import './BaseModal.css';
import { useAnimatedList } from '../../../../hooks/useAnimatedList';
import './SwipeableCard.css';
import { Icon } from '../../../DesignSystem';
import { Event } from '../../../../types/event.types';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  animationStyle?: 'slide' | 'center' | 'scale';
}

interface SwipeableCardProps {
  children: React.ReactNode;
  onLike?: () => void;
  onDismiss?: () => void;
  eventId: string;
  seriesId?: string;
  organizerId?: string;
  fullEvent?: Event; // ✅ NOUVEAU : Événement complet pour optimisation SmartCache
  disableSwipe?: boolean;
  onSwipeRight?: () => void;
  onSwipeLeft?: () => void;
  isFirstCard?: boolean; // Seule la première carte joue l'animation wiggle
  isVisible?: boolean; // La carte est-elle visible à l'écran ? (pour ne pas lancer l'animation en vue map)
  swipeContext?: SwipeOnboardingContext; // 'card' ou 'modal' pour tracking séparé de l'onboarding
}

// ✅ NOUVEAU : Interface pour les méthodes exposées
export interface SwipeableCardRef {
  triggerSwipeRight: () => Promise<void>;
  triggerSwipeLeft: () => Promise<void>;
}

// Animation wiggle pédagogique : gauche (pause) → droite (pause) → centre
// Timing avec pauses de 0.5s à chaque extrémité
const wiggleAnimation = {
  x: [0, -50, -50, 0, 50, 50, 0], // Gauche → pause → centre → droite → pause → centre
};

const wiggleTransition = {
  duration: 2.5, // Durée totale avec pauses
  times: [0, 0.15, 0.35, 0.5, 0.65, 0.85, 1], // Keyframes avec pauses
  ease: 'easeInOut',
};

// Animation des indicateurs synchronisée avec le wiggle
// ✕ apparaît quand on va à GAUCHE, ❤️ apparaît quand on va à DROITE
const dismissIndicatorWiggleAnimation = {
  opacity: [0, 1, 1, 0, 0, 0, 0], // Visible pendant la phase gauche
  scale: [0.8, 1.1, 1.1, 0.8, 0.8, 0.8, 0.8],
};

const likeIndicatorWiggleAnimation = {
  opacity: [0, 0, 0, 0, 1, 1, 0], // Visible pendant la phase droite
  scale: [0.8, 0.8, 0.8, 0.8, 1.1, 1.1, 0.8],
};

const indicatorWiggleTransition = {
  duration: 2.5,
  times: [0, 0.15, 0.35, 0.5, 0.65, 0.85, 1],
  ease: 'easeInOut',
};

// Délais avant de lancer l'animation (en ms) - différents selon le contexte
const WIGGLE_DELAY_MS: Record<SwipeOnboardingContext, number> = {
  card: 1800, // Cartes : délai plus long, l'utilisateur découvre la liste
  modal: 500, // Modales : délai court, l'utilisateur a déjà cliqué
};

// ✅ MODIFIÉ : Utiliser forwardRef + indicateurs visuels + animation wiggle + hint textuel
export const SwipeableCard = forwardRef<SwipeableCardRef, SwipeableCardProps>(({
  children,
  onLike,
  onDismiss,
  eventId,
  seriesId,
  organizerId,
  fullEvent,
  disableSwipe = false,
  isFirstCard = false, // Seule la première carte joue l'animation
  isVisible = true, // Par défaut visible (pour les modales qui n'ont pas de vue map)
  swipeContext = 'card' // Par défaut 'card', passer 'modal' pour EventModal
}, ref) => {
  const analytics = useAnalytics();
  // Ref pour tracker si l'affordance a été montrée (pas de re-render nécessaire)
  const affordanceShownRef = React.useRef(false);

  // Wraps pour tracker le swipe après affordance
  const wrappedOnLike = React.useCallback(() => {
    if (affordanceShownRef.current) {
      analytics.trackSwipeAfterAffordance(swipeContext === 'card' ? 'card' : 'modal', true);
    }
    onLike?.();
  }, [onLike, analytics, swipeContext]);

  const wrappedOnDismiss = React.useCallback(() => {
    if (affordanceShownRef.current) {
      analytics.trackSwipeAfterAffordance(swipeContext === 'card' ? 'card' : 'modal', true);
    }
    onDismiss?.();
  }, [onDismiss, analytics, swipeContext]);

  const {
    dragProps,
    style,
    indicators,
    triggerSwipeRight,
    triggerSwipeLeft
  } = useSwipeCard(eventId, wrappedOnLike, wrappedOnDismiss, seriesId, fullEvent, organizerId);

  // Hook d'onboarding pour animation wiggle (contexte séparé pour cards vs modales)
  const { shouldShowWiggle, markSwipeHintSeen } = useSwipeOnboarding(swipeContext);

  // État local pour contrôler si l'animation wiggle est en cours/terminée
  const [hasAnimated, setHasAnimated] = useState(false);

  // État pour déclencher l'animation après le délai (contrôlé manuellement)
  const [isReadyToAnimate, setIsReadyToAnimate] = useState(false);

  // ✅ NOUVEAU : Exposer les méthodes via ref
  useImperativeHandle(ref, () => ({
    triggerSwipeRight,
    triggerSwipeLeft
  }), [triggerSwipeRight, triggerSwipeLeft]);

  // Timer pour déclencher l'animation seulement quand :
  // 1. C'est la première carte (isFirstCard)
  // 2. La carte est visible (isVisible) - pas en vue map
  // 3. L'utilisateur n'a pas encore vu l'animation (shouldShowWiggle)
  // ⚠️ IMPORTANT : eventId retiré des deps pour éviter de reset le timer
  // si la première carte change pendant le délai. L'animation ne se jouera
  // que si la carte est ENCORE première au moment où le timer se termine.
  useEffect(() => {
    if (isFirstCard && isVisible && shouldShowWiggle && !hasAnimated) {
      const delay = WIGGLE_DELAY_MS[swipeContext];
      const timer = setTimeout(() => {
        setIsReadyToAnimate(true);
        affordanceShownRef.current = true;
        analytics.trackSwipeAffordanceShown(swipeContext === 'card' ? 'card' : 'modal');
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [isFirstCard, isVisible, shouldShowWiggle, hasAnimated, swipeContext]);

  // Callback après animation wiggle
  const handleWiggleComplete = () => {
    if (isReadyToAnimate && !hasAnimated) {
      setHasAnimated(true);
      markSwipeHintSeen();
    }
  };

  if (disableSwipe) {
    return <>{children}</>;
  }

  // Déterminer si on doit jouer l'animation wiggle
  // Seulement pour la première carte, après le délai, et si pas déjà animé
  const shouldAnimate = isFirstCard && isReadyToAnimate && !hasAnimated;

  return (
    <motion.div
      className="swipeable-card"
      {...dragProps}
      style={style}
      animate={shouldAnimate ? wiggleAnimation : undefined}
      transition={shouldAnimate ? wiggleTransition : undefined}
      onAnimationComplete={handleWiggleComplete}
    >
      {/* Indicateurs de swipe - positionnés à l'OPPOSÉ du sens de swipe pour rester visibles */}
      <div className="swipeable-card__indicators">
        {/* Heart à GAUCHE - visible quand on swipe à droite (like) */}
        <motion.div
          className="swipeable-card__indicator swipeable-card__indicator--like"
          style={!shouldAnimate ? {
            opacity: indicators.likeOpacity as MotionValue<number>,
            scale: indicators.likeScale as MotionValue<number>,
          } : undefined}
          animate={shouldAnimate ? likeIndicatorWiggleAnimation : undefined}
          transition={shouldAnimate ? indicatorWiggleTransition : undefined}
        >
          <Icon name="Heart" size={80} color="var(--primary-color)" />
        </motion.div>

        {/* X à DROITE - visible quand on swipe à gauche (dismiss) */}
        <motion.div
          className="swipeable-card__indicator swipeable-card__indicator--dismiss"
          style={!shouldAnimate ? {
            opacity: indicators.dismissOpacity as MotionValue<number>,
            scale: indicators.dismissScale as MotionValue<number>,
          } : undefined}
          animate={shouldAnimate ? dismissIndicatorWiggleAnimation : undefined}
          transition={shouldAnimate ? indicatorWiggleTransition : undefined}
        >
          <Icon name="X" size={80} color="var(--text-secondary)" />
        </motion.div>
      </div>

      {children}
    </motion.div>
  );
});

const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  children,
  className = '',
  animationStyle = 'slide',
}) => {
  // Gestion du scroll du body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ✅ SIMPLIFIÉ : Gestionnaire de clic simple avec protection contre les doubles appels
  const handleOverlayClick = (e: React.MouseEvent) => {
    // Ne fermer que si on clique directement sur l'overlay
    if (e.target === e.currentTarget) {
      console.log('🔒 BaseModal - Fermeture via overlay');
      onClose();
    }
  };

  // ✅ NOUVEAU : Gestionnaire de keydown pour ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="base-modal__overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleOverlayClick}
          />
          <motion.div
            className={`base-modal ${className}`}
            initial={
              animationStyle === 'center'
                ? { opacity: 0, scale: 0.95 }
                : animationStyle === 'scale'
                  ? { opacity: 0, scale: 0.92 }
                  : { opacity: 0, x: '-100%' }
            }
            animate={
              animationStyle === 'center'
                ? { opacity: 1, scale: 1, transition: { duration: 0.25, ease: 'easeOut' } }
                : animationStyle === 'scale'
                  ? { opacity: 1, scale: 1, transition: { duration: 0.3, ease: [0.34, 1.56, 0.64, 1] } }
                  : { opacity: 1, x: 0, transition: { type: "spring", damping: 30, stiffness: 300 } }
            }
            exit={
              animationStyle === 'center'
                ? { opacity: 0, scale: 0.95, transition: { duration: 0.2 } }
                : animationStyle === 'scale'
                  ? { opacity: 0, scale: 0.92, transition: { duration: 0.2, ease: 'easeIn' } }
                  : undefined
            }
            onClick={handleOverlayClick} // ✅ Fermer si clic sur la zone vide (côtés)
          >
            <div className="base-modal__container" onClick={(e) => e.stopPropagation()}>
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export const AnimatedList = ({ children }: { children: React.ReactNode }) => {
  // Convertir les enfants React en tableau avec IDs
  const items = React.Children.toArray(children).map((child, index) => ({
    id: `item-${index}`,
    content: child
  }));

  const { animatedItems, getMotionProps } = useAnimatedList(items, {
    exitDuration: 300
  });

  return (
    <AnimatePresence>
      {animatedItems.map(item => (
        <motion.div
          key={item.id}
          {...getMotionProps(item.id)}
        >
          {item.data.content}
        </motion.div>
      ))}
    </AnimatePresence>
  );
};

export default BaseModal; 